/**
 * Analytics anonymes. Aucune donnée personnelle :
 * uniquement un identifiant de session aléatoire, non persisté.
 */

export interface AnalyticsProvider {
  track(eventName: string, properties?: Record<string, unknown>): void;
}

export const EVENTS = {
  PAGE_OPEN: 'page_open',
  CAMERA_GRANTED: 'camera_granted',
  CAMERA_DENIED: 'camera_denied',
  SCAN_STARTED: 'scan_started',
  TARGET_FOUND: 'target_found',
  TARGET_LOST: 'target_lost',
  RECORDING_STARTED: 'recording_started',
  RECORDING_STOPPED: 'recording_stopped',
  VIDEO_DOWNLOADED: 'video_downloaded',
  VIDEO_SHARED: 'video_shared',
  SCREENSHOT_CAPTURED: 'screenshot_captured',
  ERROR: 'app_error',
} as const;

export class ConsoleAnalyticsProvider implements AnalyticsProvider {
  track(eventName: string, properties?: Record<string, unknown>): void {
    // eslint-disable-next-line no-console
    console.info(`[analytics] ${eventName}`, properties ?? {});
  }
}

export class NoopAnalyticsProvider implements AnalyticsProvider {
  track(): void { /* volontairement vide */ }
}

/**
 * Provider HTTP optionnel : POST {baseUrl}/api/events.
 * Utilise sendBeacon quand disponible (fiable à la fermeture de page).
 * Prêt à être remplacé par PostHog/Plausible (même interface).
 */
export class HttpAnalyticsProvider implements AnalyticsProvider {
  constructor(private readonly baseUrl: string) {}

  track(eventName: string, properties?: Record<string, unknown>): void {
    const url = `${this.baseUrl.replace(/\/$/, '')}/api/events`;
    const body = JSON.stringify({ name: eventName, properties: properties ?? {} });
    try {
      if (navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }))) return;
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    } catch {
      /* l'analytics ne doit jamais casser l'app */
    }
  }
}

export class AnalyticsService {
  private readonly sessionId: string;

  constructor(private readonly provider: AnalyticsProvider) {
    this.sessionId = AnalyticsService.randomId();
  }

  track(eventName: string, properties?: Record<string, unknown>): void {
    this.provider.track(eventName, {
      ...properties,
      sessionId: this.sessionId,
      ts: Date.now(),
    });
  }

  private static randomId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

export function createAnalyticsFromEnv(): AnalyticsService {
  const kind = import.meta.env.VITE_ANALYTICS_PROVIDER ?? 'console';
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (kind === 'http' && baseUrl) return new AnalyticsService(new HttpAnalyticsProvider(baseUrl));
  if (kind === 'noop') return new AnalyticsService(new NoopAnalyticsProvider());
  return new AnalyticsService(new ConsoleAnalyticsProvider());
}
